import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function NolaPrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Nola AI Chat Friend"
      date="NOVEMBER 2025"
    >
      <p>
        Developer: Aleksandra Rakitina
        <br />
        Contact (privacy requests): alexwadek@gmail.com
      </p>

      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “Nola AI Chat Friend” (the “App”) collects, uses,
        stores, and discloses information about users. It applies to onboarding and
        registration, the AI chat companion experience, avatar selection and profile, local
        and cloud history, subscriptions, notifications, analytics, and support. We
        intentionally limit data flows so core features function primarily on your device,
        with cloud sync and certain measurements enabled only if you choose. We do not
        operate a custom backend for chat processing; prompts go to the AI provider over
        HTTPS. By using the App, you agree to these practices. If you do not agree, uninstall
        the App and contact us for data options.
      </p>

      <div className={s.title}>2) Definitions</div>
      <p>
        To avoid ambiguity, the following terms apply. “Personal Data” means information that
        identifies or can reasonably identify you, such as an email address or account
        identifier from sign-in providers. “Processing” covers collection, storage, use,
        transmission, and deletion. “Controller” is the party setting purposes and means of
        processing (the developer listed above). “Service Providers/Processors” are third
        parties acting on our instructions, including authentication, analytics,
        notifications, cloud databases, subscription platforms, and AI model providers. “Chat
        Content” includes text you type, timestamps, and model responses. “Technical Data”
        covers device, OS, app version, locale, crash diagnostics, and notification tokens.
        “Pseudonymization” reduces direct identifiability without eliminating it.
      </p>

      <div className={s.title}>3) Controller and Contact Information</div>
      <p>
        The data controller for the App is Aleksandra Rakitina. You may contact us at
        alexwadek@gmail.com for any privacy questions or to exercise rights such as access,
        deletion, correction, restriction, objection, or portability. For your security, we
        verify requesters, typically by confirming control of the account email or by other
        suitable means. We may ask for limited additional details to confirm identity and
        prevent unauthorized disclosure. Communications about privacy are retained for
        compliance and audit purposes as allowed by law. If your request involves
        platform-level settings like subscription management or notification permissions, we
        will guide you to in-app or system settings that control those features so outcomes
        are consistent and predictable.
      </p>

      <div className={s.title}>4) Data Minimization and Purpose Limitation</div>
      <p>
        We collect only what is necessary to provide the features you select and we try to
        keep content on your device by default. Authentication uses essential identifiers to
        create an account and restore purchases. Subscriptions rely on entitlements and
        product identifiers; we do not receive card numbers. Analytics focuses on reliability
        and product improvement, not behavioral profiling. Chat Content is sent to the AI
        provider solely to generate a response. Optional Firestore sync is off until enabled.
        Retention periods are kept as short as practical, and we prefer aggregation or
        pseudonymization where full deletion is not immediately feasible due to backups or
        legal obligations. We do not sell Personal Data.
      </p>

      <div className={s.title}>5) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process: 1) account and sign-in data (email,
        provider ID, display name if available); 2) Chat Content (messages, timestamps,
        conversation IDs); 3) avatar and companion profile selections (chosen image/style,
        display name, status text); 4) technical and diagnostic information (device model,
        OS/app version, locale, time zone, crash logs in aggregated form, performance
        metrics); 5) subscription and entitlement data (product IDs, trial eligibility,
        renewal status, expiration/cancellation dates); 6) analytics events (onboarding
        steps, chat usage counters, paywall views, trial start, purchase, renewal,
        cancellation); 7) push tokens and notification preferences; 8) optional cloud history
        in Firestore if sync is enabled. We do not access photos or microphone unless you use
        a feature that explicitly requires it and you grant permission.
      </p>

      <div className={s.title}>6) Sources of Data and Voluntary Provision</div>
      <p>
        Most data comes directly from you: registration details, chat input, avatar choices,
        and settings. Additional data is generated by your use of the App, such as event logs
        that help us improve reliability. If you enable sync, your chat history is uploaded
        to a cloud database linked to your authenticated account, allowing continuity across
        devices. Providing certain items is optional, but some features depend on them:
        without authentication you cannot use account-linked sync or restore purchases, and
        without a notification token we cannot deliver push messages. You can sign out or
        disable sync at any time. Local data remains on your device until you clear it in-app
        or uninstall the App.
      </p>

      <div className={s.title}>
        7) Legal Bases and Purposes (Contract, Legitimate Interests, Consent, Legal Duty)
      </div>
      <p>
        We process data for: (a) contract performance, to deliver requested features like
        onboarding, authentication, chat, and restoring entitlements; (b) legitimate
        interests, to secure and stabilize the App, prevent fraud, and improve performance,
        balanced against your rights through minimization and opt-outs where available; (c)
        consent, for features where law or platform rules require opt-in, including certain
        notifications or analytics categories and optional Firestore sync; and (d) legal
        obligations, to comply with applicable laws and maintain necessary records. Where a
        purpose meaningfully changes, we will update this Policy and seek additional consent
        if required. We do not use Chat Content for advertising.
      </p>

      <div className={s.title}>8) AI Chat Processing and Content Handling</div>
      <p>
        When you send a message, the App transmits your prompt to an AI provider (e.g.,
        OpenAI; in the future, Anthropic) via HTTPS, either directly from the App or through
        a minimal proxy that forwards requests without creating a persistent profile of your
        content. We transmit only what is needed to generate a reply and operate quotas. We
        do not run our own content servers for the chat. On-device, your history is stored in
        SQLite with AES encryption at rest to reduce exposure if the device is lost. If you
        enable Firestore sync, conversation data and metadata are uploaded to your account’s
        cloud space, protected by platform access controls. You can clear local history in
        settings and request deletion of synced records.
      </p>

      <div className={s.title}>9) Local Storage, Encryption, and Cloud Sync</div>
      <p>
        By default, conversations are saved locally using SQLite with AES encryption at rest.
        This enables continuity without sending Chat Content to our infrastructure. If you
        turn on Firestore sync, your chat history and related metadata are transmitted to a
        cloud database associated with your sign-in. Access is controlled by authentication
        tokens and provider-managed permissions. Disabling sync stops new uploads; local
        storage continues as normal. Uninstalling the App removes local data but does not
        automatically delete cloud data you previously uploaded. To remove synced records,
        contact us from your account email and we will coordinate deletion, acknowledging
        that provider backups and replication may require additional time before removal
        fully propagates.
      </p>

      <div className={s.title}>10) Registration, Authentication, and Account Deletion</div>
      <p>
        The App supports sign-in with Apple, Google, and email/password through Firebase
        Auth. We receive a unique account identifier, your email address, and where available
        your display name to personalize the companion and link entitlements. We do not store
        passwords in plain text. You may sign out at any time to stop account-linked
        processing on that device. To request account deletion, email alexwadek@gmail.com
        from the address used for sign-in; after verification, we will delete account-linked
        data under our control, except where retention is required for legal, accounting, or
        security reasons. If you delete your account, access to paid features ends according
        to platform rules for active subscriptions.
      </p>

      <div className={s.title}>11) Subscriptions, Billing, Trials, and Entitlements</div>
      <p>
        Monetization uses in-app purchases via RevenueCat and platform billing. Current plans
        include a 3-day trial, $7.99 per week, or $29.99 per month. Free use is limited to
        approximately 20 messages per day and only the most recent chat in history. Paid
        access unlocks unlimited messages, full history, and exclusive avatars. We receive
        product identifiers, trial eligibility, renewal outcomes, expiration dates, and
        cancellation status to grant or revoke access. We do not receive payment card
        numbers. Subscription changes, refunds, and cancellations are handled through your
        platform account settings and applicable policies. Grace periods may apply when
        supported by the platform. We may retain pseudonymized transactional metadata for
        fraud prevention and accounting.
      </p>

      <div className={s.title}>12) Push Notifications and In-App Messages</div>
      <p>
        With your permission, the App may send push notifications such as reminders and
        friendly nudges (for example, “I missed you!”), and service updates about
        subscription status or security. Delivery requires a device token that we and our
        providers use solely to route messages to your device. You can adjust notification
        permissions in system settings and, where available, in-app preferences. Some notices
        that relate to service safety or billing may be delivered as permitted by law even if
        marketing-style notifications are disabled. We avoid including sensitive Chat Content
        in notifications. To prevent unintended disclosure on shared devices, consider
        limiting lock-screen previews. If you uninstall the App, your token is typically
        invalidated by the platform.
      </p>

      <div className={s.title}>13) Analytics, Performance, and Optional Attribution</div>
      <p>
        We use Firebase Analytics to measure stability and product quality, focusing on
        events like onboarding completion, avatar selection, chat usage counters, paywall
        impressions, trial start, purchase, renewal, cancellation, and error rates. Crash
        diagnostics help us fix defects across OS versions and devices. If AppsFlyer is
        enabled, it is used for high-level attribution without attaching your Chat Content or
        creating profiles for targeted advertising. Where supported, analytics are aggregated
        or pseudonymized, and retention is limited to trend analysis windows. You can reduce
        analytics via system privacy settings where available, understanding that it may
        limit our ability to troubleshoot. We do not combine analytics with third-party ad
        networks for behavioral ads.
      </p>

      <div className={s.title}>14) Data Sharing with Service Providers</div>
      <p>
        We rely on vetted providers acting on our instructions: Firebase Auth for sign-in,
        Firestore for sync (optional), FCM for notifications, RevenueCat for subscriptions
        and entitlements, analytics and crash services, and the AI model provider that
        processes your prompts. Providers are bound by terms requiring appropriate security
        and prohibiting use of Personal Data for independent marketing. We may disclose data
        when legally required by competent authorities after validating scope and
        jurisdiction. In a business transition affecting the App, successor entities will
        honor this Policy or provide notice of material changes so you can make informed
        choices. We do not sell Personal Data, and we do not share Chat Content with social
        networks.
      </p>

      <div className={s.title}>15) Security Measures</div>
      <p>
        We implement administrative, technical, and organizational safeguards appropriate to
        the risks. Communications occur over HTTPS; Firestore uses encryption at rest; local
        chat databases use AES at rest; access to operational tools is restricted by least
        privilege and logged. We monitor dependencies for known vulnerabilities and patch
        within reasonable release cycles. We separate development and production environments
        and avoid using production Personal Data for testing. No method is perfectly secure,
        but we reduce exposure through minimization, encryption, and user controls like local
        deletion and sign-out. If we become aware of an incident affecting Personal Data, we
        will investigate promptly and notify affected users and authorities where required by
        law.
      </p>

      <div className={s.title}>16) Data Retention and Deletion</div>
      <p>
        We retain Personal Data only as long as necessary for the purposes described here:
        providing the service, maintaining security, complying with legal obligations, and
        resolving disputes. Local history persists until you clear it in settings or uninstall
        the App. Synced records remain in your account’s Firestore until you disable sync or
        request deletion; full purge may depend on provider backup cycles. Subscription and
        entitlement metadata can be retained longer for accounting and fraud prevention,
        typically in pseudonymized or aggregated form. Crash and performance data are kept
        for trend analysis and then deleted or de-identified. Verified deletion requests are
        handled in a reasonable timeframe, accounting for replication and offline devices.
      </p>

      <div className={s.title}>17) International Data Transfers</div>
      <p>
        Depending on your location and the locations of our providers, your data may be
        processed in jurisdictions with different data protection rules. Where required, we
        use appropriate safeguards such as contractual commitments and platform-level
        mechanisms for cross-border transfers. Regardless of location, our providers must
        implement security measures consistent with this Policy and applicable law. You can
        contact us for information about relevant transfer mechanisms and, where appropriate,
        copies of key contractual terms that do not reveal confidential commercial
        information. By using the App, you acknowledge that data may be processed globally to
        deliver reliable service and support.
      </p>

      <div className={s.title}>18) Your Rights and Choices</div>
      <p>
        Subject to local law, you may have rights to access, correct, update, or delete
        Personal Data; request restriction; object to certain processing; or exercise data
        portability. You may also withdraw consent where processing relies on consent, such
        as optional notifications or sync. In-app, you can sign out, clear local history,
        change your companion’s name, adjust notifications, and manage theme. Subscription
        management occurs in platform account settings. To exercise other rights, email
        alexwadek@gmail.com from your sign-in address. We will verify your identity before
        responding. If we cannot fulfill a request due to legal or technical constraints, we
        will explain the reason unless prohibited.
      </p>

      <div className={s.title}>19) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect Personal Data
        from users under the age where parental consent is required by law. If you believe a
        minor has used the App without appropriate consent, contact alexwadek@gmail.com and
        we will remove the information and close the account where applicable. Parents and
        guardians can use device-level controls to limit access. If we discover that we
        inadvertently processed data from a child without proper authorization, we will
        promptly delete such data. Any future age-specific features will include consent
        mechanisms aligned with platform and legal requirements.
      </p>

      <div className={s.title}>
        20) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        The App does not use browser cookies but relies on SDKs that may store identifiers
        used for authentication, analytics, notifications, entitlement checks, and fraud
        prevention. These identifiers function similarly to cookies by enabling sessions and
        measurement. Mobile platforms offer controls like resetting device identifiers; the
        App does not rely on advertising IDs for targeted advertising. Do Not Track is a
        browser signal and generally does not apply to native apps; nonetheless, we avoid
        cross-app tracking for marketing. You can manage SDK-related permissions through
        system settings and in-app toggles where provided. We keep SDK usage scoped to the
        minimum needed for each feature.
      </p>

      <div className={s.title}>
        21) Avatar, Profile, and Personalization Disclosures
      </div>
      <p>
        You can choose a basic avatar (for example, masculine/feminine styles with a few
        visual options) and set a companion name and short status like “your friend is always
        here.” These selections are stored locally and, if you enable sync, may be stored in
        Firestore alongside your conversations. Avatars are cosmetic and do not require
        biometric processing or facial recognition. If future updates add advanced or
        animated avatars, we will update this Policy and provide in-app disclosures before
        enabling features that change data collection or processing. You may reset the avatar
        to a default style at any time and clear saved preferences via the App’s settings.
      </p>

      <div className={s.title}>
        22) Free Tier Limits, Abuse Prevention, and Fair Use
      </div>
      <p>
        The free tier enforces approximate daily limits (e.g., up to 20 messages) and
        restricts history to the most recent chat. These controls are implemented using local
        counters and, where needed, minimal identifiers to prevent abuse. Attempts to bypass
        limits may trigger protective measures to maintain service quality for everyone. We
        do not inspect Chat Content for marketing; automated checks exist solely to ensure
        platform compliance, security, and reliability. Paid plans lift message caps and
        enable full history and exclusive avatars, subject to fair-use principles designed to
        keep latency and costs manageable. We will communicate meaningful changes to limits
        according to platform rules.
      </p>

      <div className={s.title}>
        23) Feature-Specific Disclosures (AI, Sync, Paywall, Notifications)
      </div>
      <p>
        AI Chat: prompts and responses are processed only to generate replies and operate
        safety systems. Sync: disabled by default; when enabled, Firestore stores
        conversations linked to your account for multi-device continuity. Paywall: RevenueCat
        uses product identifiers and entitlements to manage trials, renewals, and grace
        periods; we do not receive card numbers. Notifications: reminders and service
        messages require a device token and your permission; preview content can be limited
        in system settings. These disclosures complement the broader sections above and aim
        to set clear expectations at the point of use.
      </p>

      <div className={s.title}>
        24) Requests Process, Verification, and Authorized Agents
      </div>
      <p>
        To submit a privacy request, email alexwadek@gmail.com from the address associated
        with your account and specify the request type (access, deletion, correction,
        restriction, objection, portability). We will verify identity and may request limited
        additional details to prevent unauthorized access or deletion. If you appoint an
        authorized agent, we may require proof of authorization and your verification.
        Responses are provided within a reasonable period. Where feasible, we supply data in
        a portable, machine-readable format. We may decline or limit requests when allowed by
        law, for example if fulfilling the request would infringe the rights of others or
        compromise security.
      </p>

      <div className={s.title}>25) EEA/UK and California Disclosures</div>
      <p>
        For users in the EEA/UK, legal bases include performance of a contract (core
        features), legitimate interests (security, stability, improvement), consent (optional
        analytics categories, notifications, sync), and legal obligations. You may lodge a
        complaint with a supervisory authority. For California residents, we do not “sell” or
        “share” personal information as defined by applicable law, and we do not use or
        disclose sensitive personal information beyond permitted purposes. You may request
        access, deletion, and correction, and you have the right to limit certain uses. We
        will not discriminate for exercising privacy rights, though deleting necessary data
        may affect feature availability.
      </p>

      <div className={s.title}>
        26) Security of Communications and Storage
      </div>
      <p>
        All communication with providers uses HTTPS. Firestore and other cloud data are
        encrypted at rest by the platform. Local chat databases use AES encryption at rest.
        Access to operational dashboards is restricted, logged, and periodically reviewed. We
        rotate credentials, apply least-privilege principles, and separate development and
        production. We avoid storing Chat Content in logs and refrain from using production
        Personal Data in test environments. While no system is flawless, our layered approach
        emphasizes minimization, encryption, controlled access, and timely patching. Users
        can improve protection by updating device software, using strong passcodes, and
        avoiding credential reuse across services.
      </p>

      <div className={s.title}>
        27) Data Accuracy, User Controls, and Settings
      </div>
      <p>
        We rely on accurate information from your selected sign-in method. You can update
        your email or display name in the authentication provider and changes will propagate
        to the App. In settings, you can manage subscription links, rename the companion,
        change avatars, clear local history, toggle notifications, and switch themes.
        Subscription management and refunds occur in platform account settings. If you
        discover inaccuracies in account-linked data, contact us with details so we can
        correct records or coordinate with the relevant provider. Some updates may take time
        to appear across devices due to caches and sync windows.
      </p>

      <div className={s.title}>
        28) Aggregation, Anonymization, and Retention Windows
      </div>
      <p>
        Where practical, analytics are aggregated, and when individual-level records are
        needed temporarily, we use pseudonymization and restrict retention. For example, we
        may count paywall impressions, trial starts, renewals, or error rates without storing
        your Chat Content. When data no longer serves operational purposes, we delete it or
        transform it so individuals are not reasonably identifiable. Trend analyses help us
        plan capacity, maintain performance, and reduce crashes. We periodically review fields
        to shorten or remove them if they do not materially contribute to reliability or user
        value, and we document these choices to ensure accountability.
      </p>

      <div className={s.title}>29) Third-Party Links and External Content</div>
      <p>
        The App may include links to external sites or documentation. When you open a link,
        you leave the App and the destination’s privacy practices apply. We do not control
        third-party content and are not responsible for their policies or security. Review
        requests for additional permissions carefully and avoid submitting sensitive Chat
        Content to external forms unless you trust the recipient. If a linked resource
        changes or becomes unavailable, we may update or remove it in a future release
        without notice to maintain safety and consistency. Using external resources is
        optional and not required for core App functionality.
      </p>

      <div className={s.title}>30) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory developments.
        The “Last Updated” date will change when updates occur. We will highlight material
        changes in-app and request consent for new uses where required. Continued use of the
        App after changes take effect signifies acceptance. We encourage you to review this
        Policy periodically, especially when enabling new features such as expanded avatar
        sets, enhanced sync, or additional notification categories. If you disagree with
        updates, you may stop using the App and request account deletion and removal of
        synced data, subject to verification and applicable retention obligations.
      </p>
    </PolicyLayout>
  );
}
